import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, filter, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/** Message shape from backend: { type, path, data } */
export interface WsProgressMessage {
  type: string;
  path: string;
  data: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class TaskProgressWebSocketService implements OnDestroy {
  private readonly wsUrl: string;
  private socket: WebSocket | null = null;
  private readonly messageSubject = new BehaviorSubject<WsProgressMessage | null>(null);
  /** All messages from the WebSocket (single connect for all). Filter by path in consumers. */
  readonly messages$ = this.messageSubject.asObservable().pipe(
    filter((m): m is WsProgressMessage => m !== null)
  );
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;

  constructor(private authService: AuthService) {
    const base = environment.apiUrl || '';
    this.wsUrl = base.replace(/^http/, 'ws').replace(/\/$/, '') + '/ws';
  }

  /** Whether WebSocket for progress is enabled (env flag). */
  get useWebSocket(): boolean {
    return (environment as { useWebSocket?: boolean }).useWebSocket === true;
  }

  /** Connect with current token. Call when user is logged in. */
  connect(): void {
    if (!this.useWebSocket) return;
    const token = this.authService.getToken();
    if (!token) return;
    if (this.socket?.readyState === WebSocket.OPEN) return;

    const url = `${this.wsUrl}?token=${encodeURIComponent(token)}`;
    try {
      this.socket = new WebSocket(url);
      this.socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WsProgressMessage;
          if (msg && typeof msg.type === 'string' && typeof msg.path === 'string') {
            this.messageSubject.next(msg);
          }
        } catch {
          // ignore non-JSON or invalid
        }
      };
      this.socket.onclose = () => {
        this.socket = null;
        if (this.reconnectAttempts < this.maxReconnectAttempts && this.authService.getToken()) {
          this.reconnectAttempts++;
          setTimeout(() => this.connect(), 2000);
        }
      };
      this.socket.onerror = () => {
        // onclose will run
      };
      this.reconnectAttempts = 0;
    } catch (e) {
      console.warn('TaskProgress WebSocket connect failed', e);
    }
  }

  /** Disconnect. Call on logout. */
  disconnect(): void {
    this.reconnectAttempts = this.maxReconnectAttempts;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  /** Stream of messages for a given path (e.g. "analysis", "notifications", or "projectId_versionId"). */
  messagesByPath(path: string): Observable<WsProgressMessage> {
    return this.messages$.pipe(filter((m) => m.path === path));
  }

  /** Stream of messages for analysis status (path === "analysis"). */
  get analysisMessages$(): Observable<WsProgressMessage> {
    return this.messagesByPath('analysis');
  }

  /** Stream of messages for notifications (path === "notifications"). */
  get notificationMessages$(): Observable<WsProgressMessage> {
    return this.messagesByPath('notifications');
  }

  /** Stream of messages for a given project/version path (projectId_versionId). */
  messagesForPath(projectId: string, versionId: string): Observable<WsProgressMessage> {
    const path = `${projectId}_${versionId}`;
    return this.messagesByPath(path);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
